<template>
  <router-link
    :to="to"
    class="nav-item group relative flex items-center transition-all duration-150"
    :class="[
      isActive 
        ? 'border-l-4 border-blue-500 bg-blue-50 text-blue-700' 
        : 'border-l-4 border-transparent text-gray-700 hover:bg-gray-50',
      collapsed ? 'justify-center py-3' : 'py-2.5 px-3'
    ]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Icon -->
    <svg 
      class="flex-shrink-0 transition-colors duration-150"
      :class="collapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'"
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="icon" />
    </svg>
    
    <!-- Label (hidden when collapsed) -->
    <span 
      v-if="!collapsed" 
      class="text-sm font-medium transition-opacity duration-150"
    >
      {{ label }}
    </span>
    
    <!-- Tooltip (only shown when collapsed) -->
    <Transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <div
        v-if="collapsed && showTooltip"
        class="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50"
        role="tooltip"
      >
        {{ label }}
      </div>
    </Transition>
  </router-link>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

interface Props {
  to: string;
  icon: string;
  label: string;
  collapsed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false
});

const route = useRoute();
const showTooltip = ref(false);

const isActive = computed(() => {
  // Exact match for root path
  if (props.to === '/' && route.path === '/') return true;
  
  // For other routes, check if current path starts with nav item path
  // Special handling for root - don't match nested routes
  if (props.to === '/') return false;
  
  return route.path === props.to || route.path.startsWith(props.to + '/');
});

// Show tooltip on hover when collapsed
const handleMouseEnter = () => {
  if (props.collapsed) {
    showTooltip.value = true;
  }
};

const handleMouseLeave = () => {
  showTooltip.value = false;
};
</script>

<style scoped>
.nav-item {
  @apply relative;
}

/* Ensure smooth transitions */
.nav-item svg,
.nav-item span {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Add hover effect to tooltip trigger */
.nav-item:hover .tooltip {
  @apply block;
}
</style>


